import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  getCurrencyList,
  getCurrencyById,
  deleteCurrencyById,
  updateCurrencyById,
  createCurrency,
  getCurrencyListByBranch,
} from "./queries";

const CurrencyContext = createContext({});

export const useCurrencyContext = () => useContext(CurrencyContext);

export const CurrencyProvider = ({ ...props }) => {
  const [currencyPerId, setCurrencyPerId] = useState({});
  const [currencyList, setCurrencyList] = useState([]);
  const [currencyListByBranch, setCurrencyListByBranch] = useState([]);

  const currencyListGetter = useCallback(async () => {
    try {
      if (currencyList.length === 0) {
        const result = await getCurrencyList();
        return setCurrencyList(result);
      }
    } catch (e) {
      console.error("currencyListGetter", e);
      throw e;
    }
  }, [currencyList]);

  const currencyByIdGetter = useCallback(
    async (id) => {
      try {
        setCurrencyPerId(await getCurrencyById(id));
      } catch (e) {
        console.error("currencyByIdGetter", e);
        throw e;
      }
    },
    [setCurrencyPerId]
  );

  const currencyListByBranchGetter = useCallback(
    async (branch_id) => {
      try {
        setCurrencyListByBranch(await getCurrencyListByBranch(branch_id));
      } catch (e) {
        console.error("currencyListByBranchGetter", e);
        throw e;
      }
    },
    [setCurrencyListByBranch]
  );

  const currencyDeleteById = useCallback(
    async (id) => {
      try {
        await deleteCurrencyById(id);
        setCurrencyList((old) => old.filter((row) => row.id !== id));
      } catch (e) {
        console.error("currencyDeleteById", e);
        throw e;
      }
    },
    [setCurrencyList]
  );

  const currencyUpdateById = useCallback(async (id, data) => {
    try {
      const updatedCurrency = await updateCurrencyById(id, data);
      setCurrencyList((old) =>
        old.map((elm) =>
          elm.id === updatedCurrency.id ? updatedCurrency : elm
        )
      );
      setCurrencyPerId(updatedCurrency);
    } catch (e) {
      console.error("currencyUpdateById", e);
      throw e;
    }
  }, []);

  const currencyCreate = useCallback(async (data) => {
    try {
      const currency = await createCurrency(data);
      setCurrencyList((old) => [...old, currency]);
    } catch (e) {
      console.error("currencyCreate", e);
      throw e;
    }
  }, []);

  const values = useMemo(
    () => ({
      currencyPerId,
      setCurrencyPerId,
      currencyListGetter,
      currencyByIdGetter,
      currencyListByBranchGetter,
      currencyListByBranch,
      setCurrencyListByBranch,
      currencyDeleteById,
      currencyList,
      currencyUpdateById,
      currencyCreate,
    }),
    [
      currencyPerId,
      setCurrencyPerId,
      currencyListGetter,
      currencyByIdGetter,
      currencyListByBranch,
      setCurrencyListByBranch,
      currencyDeleteById,
      currencyList,
      currencyUpdateById,
      currencyCreate,
    ]
  );

  return <CurrencyContext.Provider value={values} {...props} />;
};
